import * as React from "react";
import {
  Badge,
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useToast,
} from "@repo/ui";
import {
  Download,
  Eye,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  MoreHorizontal,
  Trash2,
} from "lucide-react";
import { api } from "@/lib/api/client";
import { DeleteReportDialog } from "./delete-report-dialog";

// ─── Types ──────────────────────────────────────────────────────────────────

interface SavedReport {
  id: string;
  name: string;
  file_path: string;
  format: string;
  source: string;
  file_size: number | null;
  record_count: number | null;
  created_at: string;
  created_by: string;
  users: { full_name: string } | null;
}

interface ReportsTableProps {
  sourceFilter: string;
  refreshTrigger?: number;
}

// ─── Source metadata ────────────────────────────────────────────────────────

const SOURCE_META: Record<
  string,
  { label: string; variant: "default" | "secondary" | "outline" }
> = {
  inventory: { label: "Inventory", variant: "default" },
  dispenses: { label: "Dispenses", variant: "default" },
  receipts: { label: "Receipts", variant: "default" },
  activity: { label: "Activity", variant: "secondary" },
  audit: { label: "Audit Log", variant: "secondary" },
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number | null): string {
  if (bytes == null || bytes === 0) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatAbsoluteDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ReportsTable({
  sourceFilter,
  refreshTrigger,
}: ReportsTableProps) {
  const [reports, setReports] = React.useState<SavedReport[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [deleteTarget, setDeleteTarget] = React.useState<SavedReport | null>(
    null
  );
  const { toast } = useToast();

  // ── Fetch reports ────────────────────────────────────────────────────────

  const fetchReports = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const params: Record<string, string> = { pageSize: "100" };
      if (sourceFilter && sourceFilter !== "all") {
        params.source = sourceFilter;
      }
      const result = await api.get<{ data?: { reports: SavedReport[] } }>(
        "/reports",
        { params }
      );
      setReports(result.data?.reports ?? []);
    } catch (error) {
      console.error("Failed to fetch reports:", error);
      toast({
        title: "Error",
        description: "Failed to load reports.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFilter, refreshTrigger, toast]);

  React.useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // ── Download handler ─────────────────────────────────────────────────────

  const handleDownload = async (report: SavedReport) => {
    try {
      const result = await api.get<{ data?: { signedUrl: string } }>(
        `/reports/${report.id}`
      );
      const signedUrl = result.data?.signedUrl;
      if (!signedUrl) throw new Error("No signed URL returned");

      // Open download in new tab
      window.open(signedUrl, "_blank");
    } catch (error) {
      console.error("Download failed:", error);
      toast({
        title: "Download Failed",
        description: "Could not download the report. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleOpenInBrowser = async (report: SavedReport) => {
    try {
      const result = await api.get<{ data?: { signedUrl: string } }>(
        `/reports/${report.id}`,
        {
          params: { view: "true" },
        }
      );
      const signedUrl = result.data?.signedUrl;
      if (!signedUrl) throw new Error("No signed URL returned");

      window.open(signedUrl, "_blank");
    } catch (error) {
      console.error("Open failed:", error);
      toast({
        title: "Could Not Open Report",
        description: "Could not open the report. Please try again.",
        variant: "destructive",
      });
    }
  };

  // ── Loading skeleton ─────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Card className="overflow-hidden rounded-xl">
        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Format</TableHead>
                <TableHead>Records</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Created By</TableHead>
                <TableHead className="w-[50px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-40" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-10" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-12" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-14" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-8 w-8" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    );
  }

  // ── Empty state ──────────────────────────────────────────────────────────

  if (reports.length === 0) {
    return (
      <Card className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="rounded-2xl bg-muted/50 p-4 mb-4">
          <FolderOpen className="h-10 w-10 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-1">No saved reports</h3>
        <p className="text-sm text-muted-foreground max-w-md">
          {sourceFilter !== "all"
            ? `No ${SOURCE_META[sourceFilter]?.label ?? sourceFilter} reports generated yet. Use the report builder above to create one.`
            : "No reports generated yet. Use the report builder above to generate your first report."}
        </p>
      </Card>
    );
  }

  // ── Table ────────────────────────────────────────────────────────────────

  return (
    <>
      <Card className="overflow-hidden rounded-xl" data-testid="reports-table">
        <div
          className="overflow-auto"
          style={{ maxHeight: "calc(100vh - 320px)" }}
        >
          <Table>
            <TableHeader className="sticky top-0 z-10 [&_tr]:border-b">
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Format</TableHead>
                <TableHead>Records</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Created By</TableHead>
                <TableHead className="w-[100px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.map((report) => {
                const sourceMeta = SOURCE_META[report.source] ?? {
                  label: report.source,
                  variant: "outline" as const,
                };
                const FormatIcon =
                  report.format === "pdf" ? FileText : FileSpreadsheet;

                return (
                  <TableRow
                    key={report.id}
                    data-testid={`report-row-${report.id}`}
                  >
                    <TableCell className="font-medium max-w-[250px] truncate">
                      {report.name}
                    </TableCell>
                    <TableCell>
                      <Badge variant={sourceMeta.variant} className="text-xs">
                        {sourceMeta.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <FormatIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs uppercase font-medium">
                          {report.format}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {report.record_count?.toLocaleString() ?? "-"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatFileSize(report.file_size)}
                    </TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger className="text-muted-foreground text-sm">
                            {formatRelativeDate(report.created_at)}
                          </TooltipTrigger>
                          <TooltipContent>
                            {formatAbsoluteDate(report.created_at)}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {report.users?.full_name ?? "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => handleOpenInBrowser(report)}
                              >
                                <Eye className="h-4 w-4" />
                                <span className="sr-only">Open in browser</span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Open in browser</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => handleDownload(report)}
                              >
                                <Download className="h-4 w-4" />
                                <span className="sr-only">Download</span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Download</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              data-testid={`report-actions-trigger-${report.id}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">More actions</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              data-testid={`report-delete-trigger-${report.id}`}
                              onClick={() => setDeleteTarget(report)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <DeleteReportDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        reportId={deleteTarget?.id ?? ""}
        reportName={deleteTarget?.name ?? ""}
        onSuccess={() => {
          setReports((prev) => prev.filter((r) => r.id !== deleteTarget?.id));
          setDeleteTarget(null);
        }}
      />
    </>
  );
}
